import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { format } from 'date-fns';

import { UpdateFilterPayloadInterface } from '~/attendee/interfaces/update-filter-payload.interface';
import { InputStyleEnum } from '~/component/form/input/enums/style.enum';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { generateUid } from '~/core/helpers/uid/uid.helper';
import { LocaleService } from '~/i18n/services/locale.service';

import { QueryParamsFilterEnum } from '~/attendee/helpers/result-page/query-params-filter.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { propertyHasBeenEmptied } from '~/core/helpers/lifecycle-hooks/on-changes/property-has-been-emptied.helper';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';

const DATE_FORMAT = 'yyyy-MM-dd';

export interface DateRangeInterface {
  from: Date;
  to: Date;
}

@Component({
  selector: 'app-attendee-date-range-input',
  templateUrl: './date-range-input.component.html',
  styleUrls: ['./date-range-input.component.scss'],
})
export class AttendeeDateRangeInputComponent implements OnInit, OnChanges {
  @Input() public ariaLabel: string;
  @Input() public defaultFrom?: Date;
  @Input() public defaultTo?: Date;
  @Input() public inputStyle:
    | InputStyleEnum.Shadowed
    | InputStyleEnum.Transparent = InputStyleEnum.Shadowed;
  @Input() public label?: string;
  @Input() public placeholder?: string;
  @Output() public resetDateFilter = new EventEmitter<
    UpdateFilterPayloadInterface[]
  >();
  @Output() public selected = new EventEmitter<DateRangeInterface>();
  @ViewChild(ModalComponent) public dateRangeModal: ModalComponent;

  public readonly icon = VtmnPlayIconTypeEnum;
  public readonly id = generateUid();
  public readonly modalVariant = ModalVariantEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public dateRange: DateRangeInterface;
  public dateRangeForm: FormGroup;

  constructor(private readonly localeService: LocaleService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (propertyHasBeenEmptied(changes, 'defaultFrom')) {
      this.clear();
    }
  }

  public ngOnInit(): void {
    this.dateRangeForm = new FormGroup({
      from: new FormControl(
        this.defaultFrom ? format(this.defaultFrom, DATE_FORMAT) : undefined,
        Validators.required
      ),
      to: new FormControl(
        this.defaultTo ? format(this.defaultTo, DATE_FORMAT) : undefined
      ),
    });

    this.dateRange = {
      from: this.defaultFrom,
      to: this.defaultTo,
    };

    this.selected.subscribe((value) => {
      this.dateRange = value;
      this.dateRangeModal.closeModal();
    });
  }

  public get dateRangeButtonText(): string {
    return this.getFormattedDateRange() || this.placeholder;
  }

  public get from(): AbstractControl {
    return this.dateRangeForm?.get('from');
  }

  public get fromMinDate(): string {
    return format(new Date(), DATE_FORMAT);
  }

  public get to(): AbstractControl {
    return this.dateRangeForm.get('to');
  }

  public get toMinDate(): string {
    return this.from.value || this.fromMinDate;
  }

  public get variantClass(): string {
    return `date-range-input--${this.inputStyle}`;
  }

  private formatDate(date: Date): string {
    return `${format(date, 'dd MMMM yyyy', {
      locale: this.localeService.dateLocale,
    })}`;
  }

  public clear(): void {
    this.dateRangeForm.reset();
    this.selected.emit(undefined);

    this.resetDateFilter.emit([
      { field: QueryParamsFilterEnum.START_DATE_FILTER_NAME, value: null },
      { field: QueryParamsFilterEnum.END_DATE_FILTER_NAME, value: null },
    ]);
  }

  public closeModal(): void {
    this.dateRangeModal.hide();
    this.dateRangeForm.reset();
  }

  public getFormattedDateRange(): string {
    let value: string;

    if (this.dateRange?.from) {
      value = this.formatDate(this.dateRange.from);

      if (this.dateRange.to) {
        value += ` - ${this.formatDate(this.dateRange.to)}`;
      }
    }

    return value;
  }

  public onSubmit(): void {
    if (!this.dateRangeForm.valid) {
      return;
    }

    this.selected.emit({
      from: new Date(this.from.value),
      to: this.to.value && new Date(this.to.value),
    });
  }
}
